// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — BonnieMinerium.jsx   ·   BONNIE'S MINERIUM SHELVES
// ────────────────────────────────────────────────────────────────────
// ⭐ caP'n, 2026-09-25: "the Minerium shop is just the Bonnie's Basket, just
// an easy way to access it, so combine both the stores."
//
// So there is ONE store: Bonnie's Bunni Basket (`PierMarketplace.js`). It has
// two counters — Mark plates, and these: furnishings, growers, doors, skies,
// grounds, the Hall's finishes, layouts, and the pier's own sky. The Minerium's
// shop button opens the SAME store on this counter; walking up to her wagon
// opens it on whichever counter you pick.
//
// This file was Mystic Ally's counter inside `MineriumScreen.jsx`
// (`AllysCounter`), lifted out whole. What changed on the way:
//   • It feeds itself. It subscribes to Dawgs, the shed, the owned book and
//     the holding by itself, and makes its own purchases through
//     `mineriumShop.js`, so it works on the boardwalk where there is no yard.
//   • It is a panel, not an overlay — PierMarketplace owns the chrome.
//   • `onHold` is optional. In the yard it hands a bought piece to the shed
//     ghost exactly as before; on the pier it just says where the piece went.
//   • A worn thing you own gets WEAR IT here (the pier has no wardrobe).
//
// ⚠ Every price still comes from `priceOf` / `listPriceOf` (THE TESTING TILL:
// you pay ✹1, the sticker is struck through). Never `item.priceUD` directly.
// ════════════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from "react";
import {
  ITEMS, KIND, AREAS, SHELVES, SHELF_META, BUNDLES, isWorn, isWearable, BARN_HOURS, WORN_FIELD,
  bundleSaving, bundleItemCount, LAYER, layerOf, sizeLabel,
} from "./mineriumCatalogue";
import {
  listenToStock, listenToOwned, acquireItem, acquireBundle, wearItem,
  VENDING, stockOf, NO_FUNDS_REASON,
  TESTING, priceOf, listPriceOf, bundlePriceOf, bundleListPriceOf,
} from "./mineriumShop";
import { listenToHolding, emptyHolding } from "./minerium";
import { listenToDawgs, UD_ROW } from "./upDawgs";
import { UI, uiPanel, uiBtn } from "./theme";
import { Thumb } from "./buildKit";
import { ShopPreview } from "./shopPreview";
import { sfx } from "./pierSfx";

const UD = "✹";
const fmt = (n) => (n || 0).toLocaleString();
const GREEN = UI.ok;
const INK = "#0d1408";
export const STANDARD_SKY = "sky_default";   // Open Unstable Sky — free, what everyone wears by default
const btn = (on, tint = UI.gold) => uiBtn(tint, { on });
const solidBtn = (tint = UI.gold, disabled = false) => ({
  fontFamily: UI.font, fontSize: UI.size.label, letterSpacing: UI.track.label, fontWeight: 700,
  padding: "8px 14px", borderRadius: UI.rMd, border: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  background: disabled ? UI.edge : tint,
  color: disabled ? UI.dim : INK,
});

// what a shop card calls a kind (OW-34: the Hall's worn rows read as words)
const KIND_WORD = {
  [KIND.GROWER]: "GROWER", [KIND.PROP]: "PIECE", [KIND.DOOR]: "DOOR", [KIND.SKY]: "SKY", [KIND.GROUND]: "GROUND",
  [KIND.HALL_FLOOR]: "HALL FLOOR", [KIND.HALL_WALL]: "HALL WALLS", [KIND.HALL_LIGHT]: "HALL LIGHT",
  [KIND.HALL_LAYOUT]: "HALL LAYOUT"
};
const HALL_WORN = new Set([KIND.HALL_FLOOR, KIND.HALL_WALL, KIND.HALL_LIGHT]);
const SHOP_FILTERS = [
  { id: "all",    label: "ALL",         test: () => true },
  { id: "grow",   label: "GROWERS",     test: (i) => i.kind === KIND.GROWER },
  { id: "stand",  label: "FURNISHINGS", test: (i) => i.kind === KIND.PROP && layerOf(i.id) === LAYER.STAND },
  { id: "floor",  label: "UNDERFOOT",   test: (i) => i.kind === KIND.PROP && layerOf(i.id) === LAYER.FLOOR },
  { id: "wall",   label: "WALLS",       test: (i) => layerOf(i.id) === LAYER.WALL },
  { id: "door",   label: "DOORS",       test: (i) => i.kind === KIND.DOOR },
  { id: "worn",   label: "SKIES & HALL", test: (i) => isWorn(i.id) && i.kind !== KIND.HALL_LAYOUT },
  // ⭐ OW-42 · a whole interior, ready-made
  { id: "layout", label: "HALL LAYOUTS", test: (i) => i.kind === KIND.HALL_LAYOUT },
];

// ═════════════════════════════════════════════════════════════════════
// THE PRICE TAG  —  one component, so a price is shown one way
// ─────────────────────────────────────────────────────────────────
// ⭐ caP'n's ruling has a second half that is easy to drop: the sticker
// is STILL SHOWN. "✹1" on its own teaches a player the wrong number and
// then takes it away the day testing ends. So: what you pay, big, and
// what it is worth, struck through beside it.
//
// When TESTING is off, or a thing is free, pay === list and this renders
// exactly one number — no strike-through, no leftover test chrome.
// ═════════════════════════════════════════════════════════════════════
function PriceTag({ pay, list, size = 12 }) {
  const discounted = Number(list) > Number(pay);
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
      <span style={{ fontSize: size, fontWeight: 700, color: UD_ROW.tint }}>
        {UD}{fmt(pay)}
      </span>
      {discounted && (
        <span style={{ fontSize: size - 3.5, color: UI.dim,
                       textDecoration: "line-through", textDecorationColor: "#7a4a2a" }}>
          {UD}{fmt(list)}
        </span>
      )}
    </span>
  );
}

// one line of "where does this go", for the shop card
function placeLine(it) {
  if (it.kind === KIND.SKY) return "One sky, worn everywhere — over your Minerium AND the pier. Open Unstable Sky is always free to go back to.";
  if (HALL_WORN.has(it.kind)) return "Worn in your Hall — ⚒ FURNISH › FLOOR, WALLS & LIGHT.";
  if (it.kind === KIND.HALL_LAYOUT) return "Rebuilds the inside of your Hall — walls, doorways and paint, every wing. Apply it in ⚒ FURNISH › LAYOUTS & DESIGNS; your furniture in the way goes back to the shed, and PUT IT BACK undoes it.";
  if (layerOf(it.id) === LAYER.WALL) return "Hangs in your Hall, on a plain stretch of wall.";
  if (layerOf(it.id) === LAYER.FLOOR) return "Underfoot — furniture stands on it.";
  if (it.kind === KIND.DOOR) return "Indoors, a door leads out to your yard — an arch is the way between rooms.";
  if (it.passage) return "A way through — stand it in a gap between walls.";
  if (it.areas?.length === 1) return it.areas[0] === AREAS.HALL ? "Indoors only." : "Yard only.";
  return null;
}


// ════════════════════════════════════════════════════════════════════
// THE COUNTER
// ════════════════════════════════════════════════════════════════════
export default function MineriumShelves({ uid, onHold = null }) {
  const [dawgs, setDawgs]     = useState(0);
  const [stock, setStock]     = useState({});
  const [owned, setOwned]     = useState({});
  const [holding, setHolding] = useState(emptyHolding);
  const [busy, setBusy]       = useState(false);
  const [note, setNote]       = useState(null);
  useEffect(() => listenToDawgs(uid, setDawgs), [uid]);
  useEffect(() => listenToStock(uid, setStock), [uid]);
  useEffect(() => listenToOwned(uid, setOwned), [uid]);
  useEffect(() => listenToHolding(uid, setHolding), [uid]);
  useEffect(() => { if (!note) return undefined; const t = setTimeout(() => setNote(null), 3600); return () => clearTimeout(t); }, [note]);
  const say = (ok, msg) => setNote({ ok, msg });

  const cheapest = useMemo(() => Math.min(...Object.values(ITEMS).filter((i) => listPriceOf(i.id) > 0).map((i) => priceOf(i.id))), []);
  // a pier sky with no row written is the standard one — say so on its card
  const wornNow = (it) => {
    const v = holding[WORN_FIELD[it.kind]];
    return v === it.id || (!v && it.id === STANDARD_SKY);
  };

  const onBuyItem = async (itemId, qty = 1) => {
    if (busy) return false;
    setBusy(true);
    try {
      const r = await acquireItem(uid, itemId, qty);
      if (r.ok) sfx.buy();
      const it = ITEMS[itemId];
      say(!!r.ok, r.ok
        ? `${it.name}${qty > 1 ? ` ×${qty}` : ""} — ${UD}${fmt(r.paidUD)}.${it.kind === KIND.HALL_LAYOUT ? " Apply it indoors: ⚒ FURNISH › LAYOUTS & DESIGNS." : isWearable(itemId) ? " WEAR IT whenever you like." : ""}`
        : r.message);
      return r.ok;
    } finally { setBusy(false); }
  };
  const onBuyBundle = async (bundleId) => {
    if (busy) return false;
    setBusy(true);
    try {
      const r = await acquireBundle(uid, bundleId);
      if (r.ok) sfx.buy();
      say(!!r.ok, r.ok ? `${BUNDLES[bundleId].name} — ${bundleItemCount(bundleId)} pieces for ${UD}${fmt(r.paidUD)}.` : r.message);
      return r.ok;
    } finally { setBusy(false); }
  };
  const onWear = async (itemId) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await wearItem(uid, itemId);
      if (r.ok) sfx.ui();
      const it = ITEMS[itemId];
      say(!!r.ok, r.ok ? (it.kind === KIND.SKY ? `${it.name} — look up. It's over the pier too.` : `${it.name} settled over the holding.`) : r.message);
    } finally { setBusy(false); }
  };
  // in the yard: hand the piece to the shed ghost. On the pier: say where it went.
  const hold = onHold || ((itemId) => say(true, `${ITEMS[itemId]?.name || "It"} is in your shed — ⚒ BUILD in your Minerium, or ⚒ FURNISH in your Hall.`));

  const [shelf, setShelf] = useState("fable");
  const [tab, setTab] = useState("items");
  const [justBought, setJustBought] = useState(null);
  const meta = SHELF_META[shelf];
  const open = (VENDING[shelf] || "locked") === "open";
  const bundles = Object.values(BUNDLES).filter((b) => b.shelf === shelf);
  // ⚠ The SHELF is decided by the sticker, not by today's price.
  const [kindFilter, setKindFilter] = useState("all");
  useEffect(() => { setKindFilter("all"); }, [shelf, tab]);
  const itemsAll = Object.values(ITEMS).filter((i) => i.shelf === shelf && listPriceOf(i.id) > 0);
  const items = itemsAll.filter((i) => SHOP_FILTERS.find((f) => f.id === kindFilter)?.test(i) ?? true);
  const onHoldCompat = hold;

  return (
    <div style={{ ...uiPanel(), position: "relative", margin: "18px 22px 30px", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "13px 16px", borderBottom: `1px solid ${UI.edge}`,
                    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 14, letterSpacing: ".16em", color: UI.gold, fontWeight: 700 }}>FOR YOUR MINERIUM, YOUR HALL — AND THE SKY</div>
          <div style={{ fontSize: 11, color: UI.dim, marginTop: 3 }}>
            Furnishings, growers, doors and weather. Everything takes Dawgs.
          </div>
          {TESTING && (
            <div style={{ fontSize: 10.5, color: UI.gold, marginTop: 5, letterSpacing: ".08em" }}>
              TESTING TILL · everything is {UD}1 · the real price is struck through beside it
            </div>
          )}
          {dawgs < (cheapest || 1) && (
            <div style={{ fontSize: 11, color: UI.err, marginTop: 6, lineHeight: 1.5, maxWidth: 420 }}>
              {NO_FUNDS_REASON}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 15, fontWeight: 700, color: UD_ROW.tint }}>{UD} {fmt(dawgs)}</div>
      </div>
        <div style={{ padding: "9px 16px", borderBottom: `1px solid ${UI.edge}`, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {SHELVES.map((s) => (
            <button key={s} onClick={() => setShelf(s)} style={btn(shelf === s, SHELF_META[s].tint)}>
              {SHELF_META[s].label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button onClick={() => setTab("bundles")} style={btn(tab === "bundles")}>BUNDLES</button>
          <button onClick={() => setTab("items")} style={btn(tab === "items")}>SINGLES</button>
        </div>

        <div style={{ padding: "8px 16px", fontSize: 11, color: meta.tint, letterSpacing: ".04em" }}>
          {meta.line}
        </div>
        {tab === "items" && (
          <div style={{ padding: "0 16px 8px", display: "flex", gap: 5, flexWrap: "wrap" }}>
            {SHOP_FILTERS.filter((f) => f.id === "all" || itemsAll.some(f.test)).map((f) => (
              <button key={f.id} onClick={() => setKindFilter(f.id)}
                      style={{ ...btn(kindFilter === f.id), padding: "3px 8px", fontSize: 10 }}>{f.label}</button>
            ))}
          </div>
        )}

        <div style={{ overflowY: "auto", padding: "4px 16px 16px",
                      display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(238px, 1fr))", gap: 10 }}>

          {tab === "bundles" && bundles.map((b) => {
            const s = bundleSaving(b.id);
            const pay  = bundlePriceOf(b.id);
            const list = bundleListPriceOf(b.id);
            const afford = dawgs >= pay;
            return (
              <div key={b.id} style={{ border: `1px solid ${meta.tint}44`, borderRadius: UI.rSm, padding: 11,
                                       background: UI.panelSolid, display: "flex", flexDirection: "column", gap: 7 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: meta.tint }}>{b.name}</div>
                <div style={{ fontSize: 11, color: UI.dim, lineHeight: 1.55, flex: 1 }}>{b.blurb}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {Object.entries(b.contents).map(([id, n]) => (
                    <div key={id} title={ITEMS[id]?.name} style={{ position: "relative" }}>
                      <Thumb itemId={id} size={34} />
                      <span style={{ position: "absolute", right: -3, bottom: -3, fontSize: 9.5, fontWeight: 700,
                                     color: UI.text, background: UI.panelSolid, borderRadius: 6, padding: "0 4px",
                                     border: `1px solid ${UI.edge}` }}>×{n}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <PriceTag pay={pay} list={list} size={13} />
                  {/* ⚑ COMPUTED, NEVER TYPED — bundleSaving() adds the
                      parts up, so a retuned item price can never leave a
                      lying discount on a tile. */}
                  {s.amount > 0 && (
                    <span style={{ fontSize: 10.5, color: GREEN }}>
                      SAVE {UD}{fmt(s.amount)} · {s.percent}%
                    </span>
                  )}
                </div>
                {/* ⭐ a crate that is partly for indoors says so (review) */}
                {(() => {
                  const n = Object.entries(b.contents).reduce((acc, [id, c]) =>
                    acc + (ITEMS[id] && !isWorn(id) && !ITEMS[id].areas?.includes(AREAS.YARD) ? c : 0), 0);
                  return n ? <div style={{ fontSize: 10.5, color: meta.tint }}>{n} of these go indoors — ⚒ FURNISH in your Hall.</div> : null;
                })()}
                <button disabled={!open || busy || !afford}
                        onClick={async () => { const ok = await onBuyBundle(b.id); if (ok) setJustBought({ bundle: b.id }); }}
                        style={solidBtn(meta.tint, !open || busy || !afford)}>
                  {!open ? "NOT TODAY" : afford ? `BUY · ${bundleItemCount(b.id)} PIECES` : "NOT ENOUGH DAWGS"}
                </button>
                {justBought?.bundle === b.id && (
                  <button onClick={() => onHoldCompat(Object.keys(b.contents).find((id) => ITEMS[id]?.areas?.includes(AREAS.YARD)) || Object.keys(b.contents)[0])} style={solidBtn(GREEN)}>
                    ⚒ START PLACING →
                  </button>
                )}
              </div>
            );
          })}

          {tab === "items" && items.map((it) => {
            const pay  = priceOf(it.id);
            const list = listPriceOf(it.id);
            const afford = dawgs >= pay;
            const have = isWorn(it.id) ? !!owned[it.id] : stockOf(stock, it.id);
            return (
              /* ⚠ THE TIER WAS INVISIBLE ON A SINGLE. The bundle card two
                 branches up borders itself in its shelf tint; a single was
                 hardcoded grey, so a Fable barrel and a Mythical throne sat
                 side by side in the SAME box and the only tier signal was an
                 8.5px word. Everything in every IE game carries a shelf —
                 "even a simple wooden chair… it's still Fable" — so the
                 shelf has to be visible at a glance, not read. */
              <div key={it.id} style={{ border: `1px solid ${meta.tint}44`, borderRadius: UI.rSm, padding: 11,
                                        background: UI.panelSolid, display: "flex", flexDirection: "column", gap: 7 }}>
                {/* ⭐ (2026-09-25) top half picture, bottom half words — every card, skies included */}
                <ShopPreview itemId={it.id} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: UI.text }}>{it.name}</span>
                  </span>
                  <span style={{ fontSize: 10, letterSpacing: ".14em", color: meta.tint }}>
                    {KIND_WORD[it.kind] || it.kind.toUpperCase()}{!isWorn(it.id) && sizeLabel(it.id) !== "1×1" ? ` · ${sizeLabel(it.id)}` : ""}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: UI.dim, lineHeight: 1.55, flex: 1 }}>{it.blurb}</div>
                {/* ⭐ OW-34 · where it goes, when that is not the yard */}
                {/* ⭐ OW-34 · where it goes and how it behaves, when that is not obvious */}
                {placeLine(it) && <div style={{ fontSize: 10.5, color: meta.tint }}>{placeLine(it)}</div>}
                {it.grow && (
                  <div style={{ fontSize: 10.5, color: GREEN }}>
                    {UD}{it.grow.yieldUD} every {Math.round(it.grow.ripenMs / 60000)} min · stops filling after {BARN_HOURS} h ({UD}{fmt(it.grow.yieldUD * it.grow.capCycles)})
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <PriceTag pay={pay} list={list} size={12} />
                  {!!have && <span style={{ fontSize: 10.5, color: UI.dim }}>
                    {isWorn(it.id) ? "OWNED" : `you have ${have}`}
                  </span>}
                </div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  <button disabled={!open || busy || !afford || (isWorn(it.id) && !!have)}
                          onClick={async () => { const ok = await onBuyItem(it.id, 1); if (ok && !isWorn(it.id)) setJustBought({ item: it.id }); }}
                          style={{ ...solidBtn(meta.tint, !open || busy || !afford || (isWorn(it.id) && !!have)), flex: 1 }}>
                    {isWorn(it.id) && have ? "ALREADY YOURS" : afford ? "BUY ONE" : "NOT ENOUGH DAWGS"}
                  </button>
                  {!isWorn(it.id) && afford && open && (
                    <button disabled={busy || dawgs < pay * 5}
                            onClick={async () => { const ok = await onBuyItem(it.id, 5); if (ok) setJustBought({ item: it.id }); }}
                            style={solidBtn(meta.tint, busy || dawgs < pay * 5)}>×5</button>
                  )}
                </div>
                {/* ⭐ (2026-09-25) a worn thing you own can be put on from the counter —
                    on the pier there is no wardrobe to walk to. A pier sky can also
                    be taken off again, back to the free standard one. */}
                {isWearable(it.id) && !!have && (
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    <button disabled={busy || wornNow(it)} onClick={() => onWear(it.id)}
                            style={{ ...solidBtn(GREEN, busy || wornNow(it)), flex: 1 }}>
                      {wornNow(it) ? "WEARING IT" : "WEAR IT"}
                    </button>
                    {it.kind === KIND.SKY && wornNow(it) && it.id !== STANDARD_SKY && (
                      <button disabled={busy} onClick={() => onWear(STANDARD_SKY)} style={btn(false)}>
                        BACK TO THE STANDARD SKY
                      </button>
                    )}
                  </div>
                )}
                {justBought?.item === it.id && !!have && (
                  it.areas?.includes(AREAS.YARD)
                    ? <button onClick={() => onHoldCompat(it.id)} style={solidBtn(GREEN)}>⚒ PLACE IT →</button>
                    : <div style={{ fontSize: 10.5, color: GREEN }}>In your shed — ⚒ FURNISH in your Hall to place it.</div>
                )}
              </div>
            );
          })}
        </div>
      {note && (
        <div style={{ margin: "0 16px 14px", fontSize: 11.5, color: note.ok ? GREEN : UI.err }}>{note.msg}</div>
      )}
    </div>
  );
}
